import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, useCallback, useMemo, useRef, useState, type ReactNode} from 'react'
import type {PageLimits} from '../../page-data/payloads/files'
import {
  useCommentsPanelViewActions,
  useCommentsPanelViewMode,
  useLayoutAuthorLogin,
  useLayoutCanComment,
} from '../../stores/layout-selectors'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {createPortal} from 'react-dom'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useDockedPanelMetadata,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './docked-side-panel-constants'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import {Comments} from '../comments/Comments'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {
  isIssueCommentComposerEnabled,
  IssueCommentComposer,
  type IssueCommentComposerHandle,
} from '../comments/IssueCommentComposer'
import {CommentsContext} from '../comments/CommentsContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'
import {useCommentsSortState} from '../comments/use-comments-sort-state'
import {IDS} from '@github-ui/commenting/DomElements'
import styles from './CommentsSidePanel.module.css'
import {displayModeForViewMode} from '../comments/utils/display-mode'

export interface CommentsSidePanelProps {
  pageLimits?: PageLimits
}

// Side panel requires a unique ID prefix to prevent clashing with the main page content
const panelPrefix = 'panel'

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments.
 *
 * Self-contained: sources its own data from hooks/context.
 *
 * Rendering target depends on the `updated_pull_request_header` flag:
 * - **ON**: rendered by `DockedSidePanelContent` and emits `<Panel>` inline; that
 *   parent already lives inside the docked sidebar.
 * - **OFF**: rendered as a child of `OpenCommentsPanelButton` (to inherit
 *   `PullRequestStoreProvider` scope needed by `ThreadPositioningWithStore`),
 *   and portals `<Panel>` into `DOCKED_SIDE_PANEL_CONTENT_ID` so it visually
 *   appears in the sidebar.
 *
 * In compact mode, falls back to a Dialog sidesheet regardless of flag.
 */
export const CommentsSidePanel = memo(function CommentsSidePanelComponent({pageLimits}: CommentsSidePanelProps) {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  const pullRequestAuthorLogin = useLayoutAuthorLogin()
  const canComment = useLayoutCanComment()

  // The panel's view state lives in the layout store so it survives this component unmounting
  // when the panel closes. It is mapped onto `CommentsContext` here so the comments subtree
  // stays reusable by consumers that supply their own state instead.
  const viewMode = useCommentsPanelViewMode()
  const {setCommentsPanelViewMode} = useCommentsPanelViewActions()
  const displayMode = displayModeForViewMode(viewMode)

  const commentsContextValue = useMemo(
    (): CommentsContext => ({
      viewMode,
      setViewMode: setCommentsPanelViewMode,
      commentAnchorPrefix: panelPrefix,
    }),
    [viewMode, setCommentsPanelViewMode],
  )

  // Apply panel metadata (e.g. author filter from action items)
  const panelMetadata = useDockedPanelMetadata() ?? undefined

  const {filterState, setCurrentFilterState, resetFilterStates} = useCommentsFilterState(viewMode)
  const [sort, setSort] = useCommentsSortState(displayMode, panelPrefix)

  const {
    filteredConversationItems,
    totalConversationItemsCount,
    authorList,
    allReviewThreadsResolved,
    hasCurrentUserReviewThreads,
  } = useFilteredCommentsData({displayMode, filterState, sort})

  // Apply view mode from each new panel metadata payload, if specified
  const [lastAppliedMetadata, setLastAppliedMetadata] = useState<typeof panelMetadata>()
  if (panelMetadata !== lastAppliedMetadata) {
    setLastAppliedMetadata(panelMetadata)
    if (panelMetadata?.viewMode) setCommentsPanelViewMode(panelMetadata.viewMode)
    if (panelMetadata)
      setCurrentFilterState(prev => ({
        ...prev,
        selectedAuthors: panelMetadata?.selectedAuthor ? [panelMetadata.selectedAuthor] : prev.selectedAuthors,
        hideResolvedThreads: panelMetadata?.hideResolved ?? prev.hideResolvedThreads,
      }))
  }

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => composerRef.current?.insertQuote(quotedText), [])

  const isCommentPanelFooterEnabled = isIssueCommentComposerEnabled(displayMode, canComment)
  const [composerCollapsed, setComposerCollapsed] = useState(true)
  const commentsFooter = isCommentPanelFooterEnabled ? (
    // TODO: ID-based selection is a terrible idea here. We should hunt down what this is used for and fix it
    <div id={IDS.issueCommentComposer} className={styles.SidePanelFooter}>
      <IssueCommentComposer
        ref={composerRef}
        collapsed={composerCollapsed}
        onCollapsedChange={setComposerCollapsed}
        onSubmitted={resetFilterStates}
      />
    </div>
  ) : null
  // Legacy compact Dialog renders the footer without the css-anchor-positioning
  // feature flag wrapper — preserve that to avoid behavior change.
  const commentPanelFooter = commentsFooter ? <Dialog.Footer>{commentsFooter}</Dialog.Footer> : null

  const limitExceeded = pageLimits?.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits?.reviewThreadsPaginatedLimit

  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')

  if (!isOpen) return null

  const withCommentsContext = (children: ReactNode) => (
    <CommentsContext value={commentsContextValue}>{children}</CommentsContext>
  )

  if (!isCompact) {
    const panelContent = (
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          <CommentsViewControls
            authorList={authorList}
            filterState={filterState}
            setFilterState={setCurrentFilterState}
            sort={sort}
            setSort={setSort}
            viewMode={viewMode}
          />
        </Panel.Header>
        <Panel.Body>
          <Comments
            filteredConversationItems={filteredConversationItems}
            filterState={filterState}
            handleQuoteReply={handleQuoteReply}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            totalConversationItemsCount={totalConversationItemsCount}
            allReviewThreadsResolved={allReviewThreadsResolved}
            hasCurrentUserReviewThreads={hasCurrentUserReviewThreads}
            limitCount={limitCount}
            sort={sort}
            displayMode={displayMode}
            hideCommentComposer
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>
    )

    // When the updated_pull_request_header flag is OFF, the panel is rendered
    // as a child of OpenCommentsPanelButton (so it inherits PullRequestStoreProvider
    // scope needed by ThreadPositioningWithStore). It must portal into the
    // central docked-panel target div so it visually appears in the sidebar.
    // When the flag is ON, the panel is rendered directly inside that target div
    // by DockedSidePanelContent and no portal is needed.
    if (!updatedPullRequestHeader) {
      const portalTarget = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)
      if (portalTarget) {
        return createPortal(withCommentsContext(panelContent), portalTarget)
      }
      return null
    }

    return withCommentsContext(panelContent)
  }

  // Flag-off compact carve-out: CompactSidePanelDialog skips Comments under
  // !updated_pull_request_header, so keep the legacy per-panel Dialog here.
  const compactCommentsBody = (
    <Comments
      limitExceeded={limitExceeded}
      limitCount={limitCount}
      onNavigateToDiffComment={closePanel}
      filteredConversationItems={filteredConversationItems}
      filterState={filterState}
      handleQuoteReply={handleQuoteReply}
      totalConversationItemsCount={totalConversationItemsCount}
      allReviewThreadsResolved={allReviewThreadsResolved}
      hasCurrentUserReviewThreads={hasCurrentUserReviewThreads}
      pullRequestAuthorLogin={pullRequestAuthorLogin}
      sort={sort}
      displayMode={displayMode}
      hideCommentComposer
    />
  )

  if (!updatedPullRequestHeader) {
    return withCommentsContext(
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderFooter={() => {
          return commentPanelFooter
        }}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.Comments)}</Dialog.Title>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close comments panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
            <CommentsViewControls
              authorList={authorList}
              filterState={filterState}
              setFilterState={setCurrentFilterState}
              sort={sort}
              setSort={setSort}
              viewMode={viewMode}
            />
          </Dialog.Header>
        )}
      >
        {compactCommentsBody}
      </Dialog>,
    )
  }

  // Flag-on compact: portal full content into the shared CompactSidePanelDialog.
  return withCommentsContext(
    <>
      <CompactPanel.Header>
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
          sort={sort}
          setSort={setSort}
          viewMode={viewMode}
        />
      </CompactPanel.Header>
      <CompactPanel.Content>{compactCommentsBody}</CompactPanel.Content>
      {isCommentPanelFooterEnabled && (
        <CompactPanel.Footer>
          <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
        </CompactPanel.Footer>
      )}
    </>,
  )
})
